import { Seo } from '../components/Seo.jsx';
import { PageHeader } from '../components/PageHeader.jsx';
import { ContactForm } from '../components/ContactForm.jsx';
import { usePage } from '../hooks/usePage.js';
import { useApi } from '../hooks/useApi.js';
import { fetchSiteSettings } from '../lib/api.js';

/**
 * Right-rail "Visit us" content lives in SiteSettings.contactInfo
 * (admin-managed). The form, banner and SEO copy stay hardcoded.
 */
const FALLBACK = {
  eyebrow: 'Visit us',
  rawTitle: 'Two <em>campuses</em>',
  offices: [
    {
      title: 'Admission Office 1',
      subtitle: 'Play Group to Class X',
      lines: ['House 31, Road 14/A, Dhanmondi, Dhaka', '+880 1810 199272', 'info@mlis.edu.bd'],
    },
    {
      title: 'Admission Office 2',
      subtitle: 'Classes XI–XII',
      lines: ['House 95/1, Road 11/A, Dhanmondi, Dhaka', '+880 1810 199284', 'info@mlis.edu.bd'],
    },
  ],
  hoursTitle: 'Hours',
  hoursLines: [
    'Saturday – Thursday',
    '8:00 AM – 7:00 PM',
    '',
    '<em>Closed Fridays</em>',
  ],
};

const CARD_BASE = {
  padding: 28,
  background: 'var(--cream)',
  borderLeft: '3px solid var(--gold)',
};

const CARD_DARK = {
  padding: 28,
  background: 'var(--green-900)',
  color: '#fff',
  borderLeft: '3px solid var(--gold)',
};

const CARD_TITLE = {
  fontStyle: 'italic',
  fontSize: '1.4rem',
  color: 'var(--green-800)',
  marginBottom: 8,
};

const CARD_LABEL = {
  margin: 0,
  color: 'var(--ink-3)',
  fontSize: 14,
  letterSpacing: '0.04em',
};

/** Render an array of lines as <line>…<br/> blocks. Lines may contain <em>. */
function Lines({ lines }) {
  if (!Array.isArray(lines)) return null;
  return lines.map((line, i) => (
    <span key={i}>
      <span dangerouslySetInnerHTML={{ __html: line || ' ' }} />
      {i < lines.length - 1 && <br />}
    </span>
  ));
}

export function ContactPage() {
  const head = usePage('contact');
  const { data: settings } = useApi(fetchSiteSettings, []);
  const ci = (settings?.contactInfo && typeof settings.contactInfo === 'object')
    ? settings.contactInfo
    : FALLBACK;
  const offices = Array.isArray(ci.offices) && ci.offices.length ? ci.offices : FALLBACK.offices;
  const hoursLines = Array.isArray(ci.hoursLines) && ci.hoursLines.length ? ci.hoursLines : FALLBACK.hoursLines;

  return (
    <>
      <Seo title="Contact" description={head.metaDescription} />

      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Contact' }]}
        headline={head.headline}
        lede={head.lede}
        image={head.banner}
      />

      <section>
        <div className="container">
          <div className="contact-grid">
            {/* Left — message form */}
            <div>
              <div className="section-eyebrow">Send a message</div>
              <h2 className="section-title" style={{ fontSize: '2rem' }}>
                We'd love to <em>hear from you</em>
              </h2>
              <ContactForm />
            </div>

            {/* Right — visit-us cards */}
            <div>
              <div className="section-eyebrow">{ci.eyebrow || FALLBACK.eyebrow}</div>
              <h2
                className="section-title"
                style={{ fontSize: '2rem' }}
                dangerouslySetInnerHTML={{ __html: ci.rawTitle || FALLBACK.rawTitle }}
              />
              <div style={{ display: 'grid', gap: 24, marginTop: 32 }}>
                {offices.map((o, i) => (
                  <div style={CARD_BASE} key={i}>
                    <h3 style={CARD_TITLE}>{o.title}</h3>
                    {o.subtitle && <p style={CARD_LABEL}>{o.subtitle}</p>}
                    <p style={{ margin: '14px 0 0' }}>
                      <Lines lines={o.lines} />
                    </p>
                  </div>
                ))}
                <div style={CARD_DARK}>
                  <h3 style={{ ...CARD_TITLE, color: 'var(--gold-soft)' }}>
                    {ci.hoursTitle || FALLBACK.hoursTitle}
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.85)' }}>
                    <Lines lines={hoursLines} />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
